import type { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { Tab } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import type { TabListProps } from "./interfaces";
import cn from "classnames";

const TabList: FC<TabListProps> = ({ items, active, className }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const rendered = useRef(false);
    const width = useSignal(0);
    const left = useSignal(0);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const element = ref.current.querySelector<HTMLElement>(
            `[data-tab-index="${active}"]`,
        );

        if (!element) {
            return;
        }

        const rect = element.getBoundingClientRect();

        width.value = rect.width;
        left.value = rect.left - ref.current.getBoundingClientRect().left;

        window.requestAnimationFrame(() => {
            rendered.current = true;
        });
    }, [active, left, width]);

    return (
        <div ref={ref} className={cn("relative flex", className)}>
            <div
                style={{
                    transform: `translateX(${left}px)`,
                    width: `${width}px`,
                }}
                className={cn(
                    "absolute h-full origin-left rounded-[20px] bg-sky-800 shadow-inner-bold",
                    rendered.current && "transition-all duration-100",
                )}
            />
            <Tab.List className="relative flex rounded-[20px] border border-gray-200">
                {items.map((tab, index) => (
                    <Tab
                        key={tab}
                        data-tab-index={index}
                        className={({ selected }) =>
                            cn(
                                "border-y border-transparent px-6 py-1 capitalize outline-none transition duration-100",
                                "focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white",
                                selected ? "text-white" : "text-gray-400",
                            )
                        }
                    >
                        {tab}
                    </Tab>
                ))}
            </Tab.List>
        </div>
    );
};

export default TabList;
